import { GraphicPrimitive } from './GraphicPrimitive';
import { drawEllipse, getEllipseParams } from '../../utils/drawEllipse';

export class Ellipse extends GraphicPrimitive {
  constructor(points, color, fillColor, fillType, spacing, angle, segmentLength) {
    super(color, fillColor, fillType, spacing, angle, segmentLength, 'simple', 'Ellipse');
    this.points = points;
    this.regenerateStitches();
  }

  toJSON() {
    return {
      ...super.toJSON(),
      points: this.points
    };
  }

  getControlPoints() {
    return this.points;
  }

  updateControlPoint(pointIndex, newX, newY) {
    if (pointIndex >= 0 && pointIndex < this.points.length) {
      this.points[pointIndex].x = newX;
      this.points[pointIndex].y = newY;
      this.regenerateStitches();
    }
  }

  move(dx, dy) {
    this.points.forEach(p => {
      p.x += dx;
      p.y += dy;
    });
    this.regenerateStitches();
  }

  toPolygon() {
    // Convert ellipse to polygon format for stitch generation
    const params = getEllipseParams(this.points);
    if (!params) return [];

    const { centerX, centerY, majorRadius_a, minorRadius_b, rotation } = params;

    // Approximate perimeter (Ramanujan's approximation)
    const a = majorRadius_a;
    const b = minorRadius_b;
    const perimeter = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));

    const numPoints = Math.max(16, Math.ceil(perimeter / this.segmentLength));
    const points = [];
    for (let i = 0; i <= numPoints; i++) {
      const t = (i / numPoints) * 2 * Math.PI;
      const x = majorRadius_a * Math.cos(t);
      const y = minorRadius_b * Math.sin(t);

      // Rotate and translate
      const rotatedX = x * Math.cos(rotation) - y * Math.sin(rotation) + centerX;
      const rotatedY = x * Math.sin(rotation) + y * Math.cos(rotation) + centerY;

      points.push([rotatedX, rotatedY]);
    }

    return [[[...points]]];
  }

  regenerateStitches() {
    const multiPoly = this.toPolygon();
    const parallelStitches = [];

    if (this.fillColor && this.fillType === 'parallel') {
      const params = getEllipseParams(this.points);
      if (params) {
        const { centerX, centerY, majorRadius_a, minorRadius_b } = params;
        const maxR = Math.max(majorRadius_a, minorRadius_b);
        const bounds = {
          minX: centerX - maxR,
          maxX: centerX + maxR,
          minY: centerY - maxR,
          maxY: centerY + maxR
        };

        this.generateParallelStitches(bounds, multiPoly);
        if (this.stitchLists.length > 0) {
          this.stitchLists.forEach(list => {
            list.color = this.fillColor;
            parallelStitches.push(list);
          });
        }
      }
    }

    const contourStitches = this.generateContourStitches(multiPoly);
    this.stitchLists = [...parallelStitches, ...contourStitches];
  }

  // Render the ellipse on the canvas
  renderDrawing(ctx, transform, globalStitchIndex, stitchOffset) {
    const color = this.isSelected ? 'red' : this.color;

    const params = getEllipseParams(this.points);
    if (!params) return;

    const { centerX, centerY, majorRadius_a, minorRadius_b, rotation } = params;

    const maxR = Math.max(majorRadius_a, minorRadius_b);
    const bounds = {
      minX: centerX - maxR,
      maxX: centerX + maxR,
      minY: centerY - maxR,
      maxY: centerY + maxR
    };

    this.drawFill(ctx, transform, () => {
      ctx.ellipse(centerX, centerY, majorRadius_a, minorRadius_b, rotation, 0, 2 * Math.PI);
    }, bounds, globalStitchIndex, stitchOffset);

    // Always draw stitches if they exist
    this.drawStitches(ctx, transform, globalStitchIndex, stitchOffset);

    drawEllipse(ctx, this.points, color, transform.scale);
  }

  AddPoint(point) {
    this.points.push(point);
    if (this.points.length === 3) {
      this.regenerateStitches(); // Generate stitches when shape is completed
    }
    return this.points.length == 3;
  }

  drawPoint(ctx, x, y, color, transform) {
    ctx.beginPath();
    const radius = 5 / transform.scale;
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  }

  renderActivePoints(ctx, transform) {
    if (this.points)
      this.points.forEach(p => this.drawPoint(ctx, p.x, p.y, 'red', transform));

  }

  // Render a preview of the ellipse
  renderPreview(ctx, transform) {
    this.renderDrawing(ctx, transform);
  }

  // Handle mouse events (e.g., resizing or moving the ellipse)
  handleMouseEvent(event) {
    // Placeholder for mouse event handling logic
    console.log('Mouse event:', event);
  }




  isPointInside(x, y, transform) {

    const ellipsePoints = this.points;
    const strokeTolerance = 5 / transform.scale;

    if (!ellipsePoints || ellipsePoints.length < 3) {
      return false;
    }
    const [p1, p2, p3] = ellipsePoints;

    // --- 1. Calculate Ellipse Geometry (same as in drawEllipse) ---

    // Center (C): Midpoint of P1 and P2
    const centerX = (p1.x + p2.x) / 2;
    const centerY = (p1.y + p2.y) / 2;

    // Major Axis Radius (a)
    const dx_p1p2 = p2.x - p1.x;
    const dy_p1p2 = p2.y - p1.y;
    const majorRadius_a = Math.hypot(dx_p1p2, dy_p1p2) / 2;

    // Rotation angle of the major axis
    const rotation = Math.atan2(dy_p1p2, dx_p1p2);

    // --- 2. Calculate Minor Radius (b) using P3 ---

    // Transform P3 to the ellipse's local coordinate system (centered at origin, un-rotated)
    const px3 = p3.x - centerX;
    const py3 = p3.y - centerY;

    const cos_neg_rot = Math.cos(-rotation);
    const sin_neg_rot = Math.sin(-rotation);

    const xp3 = px3 * cos_neg_rot - py3 * sin_neg_rot;
    const yp3 = px3 * sin_neg_rot + py3 * cos_neg_rot;

    // Solve for minorRadius_b using the ellipse equation: (x²/a²) + (y²/b²) = 1
    if (Math.abs(majorRadius_a) < 1e-12) {
      return false; // Cannot define an ellipse.
    }

    const term = 1 - (xp3 * xp3) / (majorRadius_a * majorRadius_a);

    if (term <= 0) {
      // P3 is on or outside the major axis, ellipse is not well-defined.
      return false;
    }

    const minorRadius_b = Math.sqrt((yp3 * yp3) / term);

    // --- 3. Check the Point ---

    // Transform the pointToCheck to the ellipse's local coordinate system
    const px_check = x - centerX;
    const py_check = y - centerY;

    const xp_check = px_check * cos_neg_rot - py_check * sin_neg_rot;
    const yp_check = px_check * sin_neg_rot + py_check * cos_neg_rot;

    // --- 4. Apply the Ellipse Equation ---

    // Calculate the value of the ellipse equation for the transformed point
    const value = (xp_check * xp_check) / (majorRadius_a * majorRadius_a) +
      (yp_check * yp_check) / (minorRadius_b * minorRadius_b);

    // Check if the point is inside the filled ellipse or near the stroke
    // For filled shapes: value <= 1 means inside
    // For stroke: we need to check if it's near the edge (value ≈ 1)
    // Calculate tolerance relative to the ellipse size
    const avgRadius = (majorRadius_a + minorRadius_b) / 2;
    const relativeTolerance = strokeTolerance / avgRadius;

    // Check if inside the filled area OR near the stroke edge
    return value <= 1 || Math.abs(value - 1) < relativeTolerance;
  }

}